(function() {
  'use strict';

  // ===========================================
  // CONFIGURATION
  // ===========================================
  const STOREFRONT_TOKEN = 'e9b32edd766654d5ebd887a4dbc5a8b4';
  const STOREFRONT_URL = 'https://half-bakd.myshopify.com/api/2024-10/graphql.json';
  const MAX_CONCURRENT_DOWNLOADS = 4;
  const LARGE_DOWNLOAD_THRESHOLD = 25;
  const BACK_TO_TOP_THRESHOLD = 400;

  const CATEGORY_ORDER = [
    "Sumo Gummies",
    "Sour'd Sumo Gummies",
    "THC-P Gummies",
    "5G Disposables",
    "4G Disposables",
    "3G Disposables",
    "2G Disposables",
    "1G Live Resin Disposables",
    "Sauce D Blends",
    "2G Cartridges",
    "2G Concentrates",
    "2G Hash Holes Prerolls",
    "Primo Gummies",
    "THC-A Cartridges",
    "THC-A Disposables",
    "1G Disposables",
    "Other"
  ];

  // ===========================================
  // STATE
  // ===========================================
  let allCOAs = [];
  let selectedItems = new Set();
  let currentFilter = 'all';
  let searchTerm = '';
  let jsZipLoaded = false;
  let jsZipPromise = null;
  let expandedCategories = new Set();
  let downloadCancelled = false;

  // ===========================================
  // URL STATE PERSISTENCE
  // ===========================================
  function getURLParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      search: params.get('search') || '',
      state: params.get('state') || 'all',
      expanded: params.get('expanded') ? params.get('expanded').split(',') : [],
      batch: params.get('batch') || ''  // Deep link to specific batch
    };
  }

  function updateURLParams() {
    const params = new URLSearchParams();

    if (searchTerm) {
      params.set('search', searchTerm);
    }
    if (currentFilter !== 'all') {
      params.set('state', currentFilter);
    }
    if (expandedCategories.size > 0) {
      params.set('expanded', Array.from(expandedCategories).join(','));
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newURL);
  }

  function restoreFromURL() {
    const params = getURLParams();

    if (params.search) {
      searchTerm = params.search;
      const searchInput = document.getElementById('searchInput-v3');
      if (searchInput) searchInput.value = searchTerm;
    }

    if (params.state && params.state !== 'all') {
      currentFilter = params.state;
      document.querySelectorAll('.state-tab-v3').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.state === currentFilter);
      });
    }

    if (params.expanded.length > 0) {
      params.expanded.forEach(cat => expandedCategories.add(cat));
    }
  }

  // ===========================================
  // LAZY LOAD JSZIP
  // ===========================================
  function loadJSZip() {
    if (jsZipLoaded) return Promise.resolve(window.JSZip);
    if (jsZipPromise) return jsZipPromise;

    jsZipPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = () => {
        jsZipLoaded = true;
        resolve(window.JSZip);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return jsZipPromise;
  }

  // ===========================================
  // STOREFRONT API
  // ===========================================
  async function storefrontRequest(query, variables = {}) {
    const response = await fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query, variables })
    });
    return response.json();
  }

  // ===========================================
  // PROGRESSIVE DATA FETCHING
  // ===========================================
  async function fetchAllCOAs(onBatchReceived) {
    const query = `
      query getCOAs($first: Int!, $after: String) {
        metaobjects(type: "coa_certificate", first: $first, after: $after) {
          edges {
            node {
              handle
              fields {
                key
                value
                reference {
                  ... on Collection {
                    title
                    handle
                  }
                  ... on ProductVariant {
                    title
                    product {
                      title
                      handle
                    }
                  }
                  ... on GenericFile {
                    url
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    let allItems = [];
    let hasNext = true;
    let cursor = null;
    let batchNumber = 0;

    while (hasNext) {
      const result = await storefrontRequest(query, { first: 50, after: cursor });
      const data = result?.data?.metaobjects;

      if (!data) {
        console.error('Failed to fetch COAs:', result);
        break;
      }

      const batchItems = [];
      for (const edge of data.edges) {
        const node = edge.node;
        const coa = {
          handle: node.handle,
          batch_number: '',
          sample_id: '',
          ships_to_states: [],
          collection: '',
          collection_handle: '',
          product_title: '',
          product_handle: '',
          variant_title: '',
          pdf_url: '',
          pdf_download_url: ''
        };

        // Collect both PDF sources
        let shopifyPdfUrl = '';
        let externalPdfUrl = '';

        for (const field of node.fields) {
          const key = field.key;
          const value = field.value;
          const ref = field.reference;

          if (key === 'batch_number') coa.batch_number = value || '';
          else if (key === 'sample_id') coa.sample_id = value || '';
          else if (key === 'ships_to_states') {
            try {
              coa.ships_to_states = value ? JSON.parse(value) : [];
            } catch (e) {
              coa.ships_to_states = [];
            }
          }
          else if (key === 'collection' && ref) {
            coa.collection = ref.title || '';
            coa.collection_handle = ref.handle || '';
          }
          else if (key === 'flavor_variant' && ref) {
            coa.variant_title = ref.title || '';
            if (ref.product) {
              coa.product_title = ref.product.title || '';
              coa.product_handle = ref.product.handle || '';
            }
          }
          else if (key === 'pdf_file' && ref && ref.url) shopifyPdfUrl = ref.url;
          else if (key === 'pdf_url' && value) externalPdfUrl = value;
        }

        // pdf_url: PharmLabs preferred — for View PDF links, share/copy, CSV export
        // (browser navigation & clipboard — no CORS needed)
        coa.pdf_url = externalPdfUrl || shopifyPdfUrl;

        // pdf_download_url: Shopify CDN preferred — for fetch()-based downloads & ZIP
        // (CDN sends CORS headers, PharmLabs does not)
        coa.pdf_download_url = shopifyPdfUrl || externalPdfUrl;
        coa.category = deriveCategory(coa);
        coa.type_badge = getTypeBadge(coa.ships_to_states);
        coa.state_badges = getStateBadges(coa.ships_to_states);
        batchItems.push(coa);
      }

      allItems = allItems.concat(batchItems);
      batchNumber++;

      if (onBatchReceived) {
        onBatchReceived(allItems, batchNumber, !data.pageInfo.hasNextPage);
      }

      hasNext = data.pageInfo.hasNextPage;
      cursor = data.pageInfo.endCursor;
    }

    return allItems;
  }

  // ===========================================
  // CATEGORY DERIVATION
  // ===========================================
  function deriveCategory(coa) {
    const productTitle = (coa.product_title || '').toLowerCase();
    const collection = (coa.collection || '').toLowerCase();

    if (collection.includes('sumo gummies') || productTitle.includes('sumo gummies')) {
      if (collection.includes('sour') || productTitle.includes('sour')) return "Sour'd Sumo Gummies";
      return 'Sumo Gummies';
    }

    if (productTitle.includes('thc-p') || productTitle.includes('thcp')) {
      if (productTitle.includes('gummies') || collection.includes('gummies')) return 'THC-P Gummies';
    }

    if (collection.includes('primo')) {
      if (collection.includes('gummies') || productTitle.includes('gummies')) return 'Primo Gummies';
      if (collection.includes('cartridge') || productTitle.includes('cart')) return '2G Cartridges';
      if (collection.includes('disposable') || productTitle.includes('disposable')) return '3G Disposables';
    }

    if (productTitle.includes('5g')) return '5G Disposables';
    if (productTitle.includes('4g')) return '4G Disposables';
    if (productTitle.includes('3g')) return '3G Disposables';
    if (productTitle.includes('2g') && productTitle.includes('disposable')) return '2G Disposables';
    if (productTitle.includes('1g')) {
      if (productTitle.includes('live resin')) return '1G Live Resin Disposables';
      return '1G Disposables';
    }

    if (productTitle.includes('cartridge') || productTitle.includes('cart')) {
      if (collection.includes('thc-a') || productTitle.includes('thca')) return 'THC-A Cartridges';
      return '2G Cartridges';
    }

    if (productTitle.includes('hash') || collection.includes('hash')) return '2G Hash Holes Prerolls';
    if (productTitle.includes('concentrate') || collection.includes('diamond')) return '2G Concentrates';

    if (collection.includes('sauce')) {
      if (collection.includes('exotix')) return '4G Disposables';
      return 'Sauce D Blends';
    }

    if (collection.includes('private reserve')) return '1G Live Resin Disposables';

    if (collection.includes('thc-a')) {
      if (collection.includes('disposable')) return 'THC-A Disposables';
      if (collection.includes('cartridge')) return 'THC-A Cartridges';
    }

    if (collection.includes('sumo disposable')) return '5G Disposables';

    return 'Other';
  }

  function getTypeBadge(shipsToStates) {
    // Returns the primary badge based on shipping states
    if (!shipsToStates || shipsToStates.length === 0) {
      return { label: 'FED', class: 'type-federal-v3' };
    }
    // If ships to all states, show "ALL"
    if (shipsToStates.length >= 3) {
      return { label: 'ALL', class: 'type-all-v3' };
    }
    // Show the states it ships to
    return { label: shipsToStates.join('/'), class: 'type-limited-v3' };
  }

  function getStateBadges(shipsToStates) {
    // Returns array of individual state badges for display
    if (!shipsToStates || shipsToStates.length === 0) {
      return [{ label: 'FED', class: 'type-federal-v3' }];
    }
    return shipsToStates.map(state => ({
      label: state,
      class: `type-${state.toLowerCase()}-v3`
    }));
  }

  function coaShipsToState(coa, stateFilter) {
    // Check if a COA ships to a specific state
    if (stateFilter === 'all') return true;
    if (!coa.ships_to_states || coa.ships_to_states.length === 0) return false;
    return coa.ships_to_states.includes(stateFilter);
  }

  function sanitizeCategory(cat) {
    return cat.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  }

  // Filter COAs by current state and search filters
  function getFilteredCOAs(coaList = allCOAs) {
    return coaList.filter(coa => {
      if (currentFilter !== 'all' && !coaShipsToState(coa, currentFilter)) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const searchFields = [coa.batch_number, coa.product_title, coa.sample_id, coa.collection, coa.variant_title].join(' ').toLowerCase();
        if (!searchFields.includes(term)) return false;
      }
      return true;
    });
  }

  // ===========================================
  // RENDERING
  // ===========================================
  function renderCOAs() {
    const wrapper = document.getElementById('accordionWrapper-v3');
    const noResults = document.getElementById('noResults-v3');
    const visibleCount = document.getElementById('visibleCount-v3');
    const totalCount = document.getElementById('totalCount-v3');

    totalCount.textContent = allCOAs.length;

    const filteredCOAs = getFilteredCOAs();

    visibleCount.textContent = filteredCOAs.length;

    const clearBtn = document.getElementById('clearSearch-v3');
    if (clearBtn) {
      clearBtn.style.display = searchTerm ? 'flex' : 'none';
    }

    if (filteredCOAs.length === 0) {
      wrapper.innerHTML = '';
      noResults.classList.remove('hidden-v3');
      updateExpandCollapseButtons(0);
      return;
    }

    noResults.classList.add('hidden-v3');

    const byCategory = {};
    for (const coa of filteredCOAs) {
      const cat = coa.category || 'Other';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(coa);
    }

    function sortCOAsAlphabetically(coas) {
      return coas.sort((a, b) => {
        const productA = (a.product_title || '').toLowerCase();
        const productB = (b.product_title || '').toLowerCase();
        if (productA < productB) return -1;
        if (productA > productB) return 1;

        const variantA = (a.variant_title || '').toLowerCase();
        const variantB = (b.variant_title || '').toLowerCase();
        if (variantA < variantB) return -1;
        if (variantA > variantB) return 1;

        return 0;
      });
    }

    function renderCategory(cat, coas) {
      const catId = sanitizeCategory(cat);
      const isExpanded = expandedCategories.has(catId);
      const expandedClass = isExpanded ? 'expanded-v3' : '';
      const coasWithPDF = coas.filter(c => c.pdf_url).length;

      // Group COAs by product (flavor), then by variant
      const byProduct = {};
      for (const coa of coas) {
        const productKey = coa.product_title || 'Unknown Product';
        if (!byProduct[productKey]) byProduct[productKey] = {};
        const variantKey = (coa.variant_title && coa.variant_title !== 'Default Title')
          ? coa.variant_title
          : 'Default';
        if (!byProduct[productKey][variantKey]) byProduct[productKey][variantKey] = [];
        byProduct[productKey][variantKey].push(coa);
      }

      // Sort products alphabetically
      const sortedProducts = Object.keys(byProduct).sort((a, b) => a.localeCompare(b));

      let html = `
        <div class="accordion-item-v3 ${expandedClass}" data-collection="${catId}" data-category="${cat}" role="region" aria-labelledby="header-${catId}">
          <div class="accordion-header-v3"
               id="header-${catId}"
               data-category="${cat}"
               role="button"
               tabindex="0"
               aria-expanded="${isExpanded}"
               aria-controls="content-${catId}">
            <div class="accordion-left-v3">
              <span class="accordion-chevron-v3" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </span>
              <label class="accordion-checkbox-v3" onclick="event.stopPropagation()">
                <input type="checkbox"
                       data-collection="${catId}"
                       class="collection-select-all-v3"
                       aria-label="Select all ${cat} COAs">
              </label>
              <span class="accordion-title-v3">${cat}</span>
            </div>
            <div class="accordion-right-v3">
              <button type="button"
                      class="btn-download-category-v3"
                      data-category="${catId}"
                      onclick="event.stopPropagation()"
                      aria-label="Download all ${cat} PDFs"
                      ${coasWithPDF === 0 ? 'disabled' : ''}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span class="download-cat-text">Download All</span>
              </button>
              <span class="accordion-count-v3">${coas.length} COA${coas.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div class="accordion-content-v3" id="content-${catId}" role="region" aria-labelledby="header-${catId}">
            <div class="nested-accordions-v3">
      `;

      // Render each product (flavor) as a sub-accordion
      for (const product of sortedProducts) {
        const variants = byProduct[product];
        const sortedVariants = Object.keys(variants).sort((a, b) => {
          // Sort variants: try numeric sort first, then alphabetical
          const numA = parseInt(a);
          const numB = parseInt(b);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          if (a === 'Default') return -1;
          if (b === 'Default') return 1;
          return a.localeCompare(b);
        });

        const productId = sanitizeCategory(catId + '-' + product);
        const productCoaCount = Object.values(variants).flat().length;
        const variantCount = sortedVariants.length;
        const showVariantLevel = variantCount > 1 || (variantCount === 1 && sortedVariants[0] !== 'Default');

        html += `
              <div class="flavor-accordion-v3" data-flavor="${productId}">
                <div class="flavor-header-v3"
                     role="button"
                     tabindex="0"
                     aria-expanded="false">
                  <span class="flavor-chevron-v3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </span>
                  <span class="flavor-title-v3">${product}</span>
                  <span class="flavor-count-v3">${productCoaCount} COA${productCoaCount !== 1 ? 's' : ''}</span>
                </div>
                <div class="flavor-content-v3">
        `;

        // Render variants within each product
        for (const variant of sortedVariants) {
          const variantCoas = variants[variant];
          const variantId = sanitizeCategory(productId + '-' + variant);
          const displayVariant = variant === 'Default' ? 'All Variants' : variant;

          if (showVariantLevel) {
            html += `
                  <div class="variant-accordion-v3" data-variant="${variantId}">
                    <div class="variant-header-v3"
                         role="button"
                         tabindex="0"
                         aria-expanded="false">
                      <span class="variant-chevron-v3">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </span>
                      <span class="variant-title-v3">${displayVariant}</span>
                      <span class="variant-count-v3">${variantCoas.length} COA${variantCoas.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div class="variant-content-v3">
            `;
          }

          // Render COA table for this variant
          html += `
                      <table class="coa-table-v3 desktop-view-v3 nested-table-v3" role="table">
                        <thead>
                          <tr>
                            <th width="40" scope="col"><span class="visually-hidden">Select</span></th>
                            <th scope="col">Batch ID</th>
                            <th scope="col">Sample ID</th>
                            <th scope="col">Type</th>
                            <th scope="col">COA</th>
                            <th width="40" scope="col"><span class="visually-hidden">Share</span></th>
                          </tr>
                        </thead>
                        <tbody>
          `;

          for (const coa of variantCoas) {
            const isSelected = selectedItems.has(coa.handle);
            html += `
                          <tr class="${isSelected ? 'selected-v3' : ''}" data-handle="${coa.handle}" data-collection="${catId}">
                            <td>
                              <input type="checkbox"
                                     class="coa-checkbox-v3"
                                     data-handle="${coa.handle}"
                                     data-collection="${catId}"
                                     ${isSelected ? 'checked' : ''}
                                     aria-label="Select ${coa.batch_number || 'COA'}">
                            </td>
                            <td class="batch-cell-v3">${coa.batch_number || 'N/A'}</td>
                            <td class="sample-cell-v3">${coa.sample_id || '-'}</td>
                            <td><span class="type-badge-v3 ${coa.type_badge.class}">${coa.type_badge.label}</span></td>
                            <td>
                              ${coa.pdf_url
                                ? `<a href="${coa.pdf_url}" target="_blank" rel="noopener" class="view-pdf-link-v3">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                      <polyline points="14 2 14 8 20 8"></polyline>
                                    </svg>
                                    View PDF
                                  </a>`
                                : '<span class="no-pdf-v3">-</span>'}
                            </td>
                            <td>
                              ${coa.pdf_url
                                ? `<button type="button"
                                      class="share-link-btn-v3"
                                      data-pdf-url="${coa.pdf_url}"
                                      data-batch="${coa.batch_number || ''}"
                                      title="Copy PDF link">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                    </svg>
                                  </button>`
                                : ''}
                            </td>
                          </tr>
            `;
          }

          html += `
                        </tbody>
                      </table>
                      <!-- Mobile Card View for this variant -->
                      <div class="coa-cards-v3 mobile-view-v3 nested-cards-v3">
          `;

          for (const coa of variantCoas) {
            const isSelected = selectedItems.has(coa.handle);
            html += `
                        <div class="coa-card-v3 ${isSelected ? 'selected-v3' : ''}" data-handle="${coa.handle}" data-collection="${catId}">
                          <div class="coa-card-header-v3">
                            <label class="coa-card-checkbox-v3">
                              <input type="checkbox"
                                     class="coa-checkbox-v3"
                                     data-handle="${coa.handle}"
                                     data-collection="${catId}"
                                     ${isSelected ? 'checked' : ''}>
                            </label>
                            <div class="coa-card-title-v3">
                              <span class="coa-card-batch-v3">${coa.batch_number || 'N/A'}</span>
                            </div>
                            <span class="type-badge-v3 ${coa.type_badge.class}">${coa.type_badge.label}</span>
                          </div>
                          <div class="coa-card-details-v3">
                            <div class="coa-card-row-v3">
                              <span class="coa-card-label-v3">Sample:</span>
                              <span class="coa-card-value-v3">${coa.sample_id || '-'}</span>
                            </div>
                          </div>
                          <div class="coa-card-actions-v3">
                            ${coa.pdf_url
                              ? `<a href="${coa.pdf_url}" target="_blank" rel="noopener" class="view-pdf-link-v3">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                  </svg>
                                  View PDF
                                </a>`
                              : '<span class="no-pdf-v3">No PDF</span>'}
                            ${coa.pdf_url
                              ? `<button type="button"
                                    class="share-link-btn-v3"
                                    data-pdf-url="${coa.pdf_url}"
                                    data-batch="${coa.batch_number || ''}"
                                    title="Copy PDF link">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                  </svg>
                                </button>`
                              : ''}
                          </div>
                        </div>
            `;
          }

          html += `
                      </div>
          `;

          if (showVariantLevel) {
            html += `
                    </div>
                  </div>
            `;
          }
        }

        html += `
                </div>
              </div>
        `;
      }

      html += `
            </div>
          </div>
        </div>
      `;

      return html;
    }

    let html = '';
    let categoryCount = 0;

    for (const cat of CATEGORY_ORDER) {
      const coas = byCategory[cat];
      if (!coas || coas.length === 0) continue;
      html += renderCategory(cat, sortCOAsAlphabetically(coas));
      categoryCount++;
    }

    for (const cat of Object.keys(byCategory)) {
      if (CATEGORY_ORDER.includes(cat)) continue;
      html += renderCategory(cat, sortCOAsAlphabetically(byCategory[cat]));
      categoryCount++;
    }

    wrapper.innerHTML = html;
    attachEventListeners();
    updateExpandCollapseButtons(categoryCount);
  }

  function updateExpandCollapseButtons(categoryCount) {
    const expandAllBtn = document.getElementById('expandAll-v3');
    const collapseAllBtn = document.getElementById('collapseAll-v3');

    if (expandAllBtn && collapseAllBtn) {
      const hasCategories = categoryCount > 0;
      expandAllBtn.disabled = !hasCategories;
      collapseAllBtn.disabled = !hasCategories;
    }
  }

  // ===========================================
  // EVENT LISTENERS
  // ===========================================
  function attachEventListeners() {
    // Category accordion toggle with keyboard support
    document.querySelectorAll('.accordion-header-v3').forEach(header => {
      header.addEventListener('click', function(e) {
        if (e.target.type === 'checkbox' || e.target.closest('.btn-download-category-v3')) return;
        toggleAccordion(this);
      });

      header.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (e.target.type === 'checkbox') return;
          toggleAccordion(this);
        }
      });
    });

    // Flavor (product) sub-accordion toggle
    document.querySelectorAll('.flavor-header-v3').forEach(header => {
      header.addEventListener('click', function(e) {
        const parent = this.closest('.flavor-accordion-v3');
        const isExpanded = parent.classList.contains('expanded-v3');
        parent.classList.toggle('expanded-v3');
        this.setAttribute('aria-expanded', !isExpanded);
      });

      header.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const parent = this.closest('.flavor-accordion-v3');
          const isExpanded = parent.classList.contains('expanded-v3');
          parent.classList.toggle('expanded-v3');
          this.setAttribute('aria-expanded', !isExpanded);
        }
      });
    });

    // Variant sub-accordion toggle
    document.querySelectorAll('.variant-header-v3').forEach(header => {
      header.addEventListener('click', function(e) {
        const parent = this.closest('.variant-accordion-v3');
        const isExpanded = parent.classList.contains('expanded-v3');
        parent.classList.toggle('expanded-v3');
        this.setAttribute('aria-expanded', !isExpanded);
      });

      header.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const parent = this.closest('.variant-accordion-v3');
          const isExpanded = parent.classList.contains('expanded-v3');
          parent.classList.toggle('expanded-v3');
          this.setAttribute('aria-expanded', !isExpanded);
        }
      });
    });

    // Collection select-all checkbox
    document.querySelectorAll('.collection-select-all-v3').forEach(cb => {
      cb.addEventListener('change', function() {
        toggleCollectionSelect(this);
      });
    });

    // Individual COA checkbox (both table and card views)
    document.querySelectorAll('.coa-checkbox-v3').forEach(cb => {
      cb.addEventListener('change', function() {
        const handle = this.dataset.handle;
        // Update all checkboxes with same handle (table and card)
        document.querySelectorAll(`.coa-checkbox-v3[data-handle="${handle}"]`).forEach(checkbox => {
          checkbox.checked = this.checked;
        });

        if (this.checked) {
          selectedItems.add(handle);
          document.querySelectorAll(`[data-handle="${handle}"]`).forEach(el => {
            if (el.tagName === 'TR' || el.classList.contains('coa-card-v3')) {
              el.classList.add('selected-v3');
            }
          });
        } else {
          selectedItems.delete(handle);
          document.querySelectorAll(`[data-handle="${handle}"]`).forEach(el => {
            if (el.tagName === 'TR' || el.classList.contains('coa-card-v3')) {
              el.classList.remove('selected-v3');
            }
          });
        }
        updateSelectionUI();
      });
    });

    // Download by category buttons
    document.querySelectorAll('.btn-download-category-v3').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        const catId = this.dataset.category;
        downloadCategoryPDFs(catId);
      });
    });

    // Share link buttons - copy PDF URL
    document.querySelectorAll('.share-link-btn-v3').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        const pdfUrl = this.dataset.pdfUrl;
        const batchNumber = this.dataset.batch;
        if (pdfUrl) {
          copyPdfLink(pdfUrl, batchNumber);
        }
      });
    });
  }

  function toggleAccordion(header) {
    const item = header.closest('.accordion-item-v3');
    const catId = item.dataset.collection;
    const isExpanded = item.classList.toggle('expanded-v3');

    header.setAttribute('aria-expanded', isExpanded);

    if (isExpanded) {
      expandedCategories.add(catId);
    } else {
      expandedCategories.delete(catId);
    }

    updateURLParams();
  }

  function expandAllAccordions() {
    document.querySelectorAll('.accordion-item-v3').forEach(item => {
      const catId = item.dataset.collection;
      const header = item.querySelector('.accordion-header-v3');
      item.classList.add('expanded-v3');
      header.setAttribute('aria-expanded', 'true');
      expandedCategories.add(catId);
    });
    updateURLParams();
  }

  function collapseAllAccordions() {
    document.querySelectorAll('.accordion-item-v3').forEach(item => {
      const catId = item.dataset.collection;
      const header = item.querySelector('.accordion-header-v3');
      item.classList.remove('expanded-v3');
      header.setAttribute('aria-expanded', 'false');
      expandedCategories.delete(catId);
    });
    updateURLParams();
  }

  function toggleCollectionSelect(checkbox) {
    const item = checkbox.closest('.accordion-item-v3');
    const coaCheckboxes = item.querySelectorAll('.coa-checkbox-v3');

    coaCheckboxes.forEach(coaCb => {
      coaCb.checked = checkbox.checked;
      const handle = coaCb.dataset.handle;
      if (checkbox.checked) {
        selectedItems.add(handle);
      } else {
        selectedItems.delete(handle);
      }
    });

    // Update visual selection state
    item.querySelectorAll('tr[data-handle], .coa-card-v3[data-handle]').forEach(el => {
      if (checkbox.checked) {
        el.classList.add('selected-v3');
      } else {
        el.classList.remove('selected-v3');
      }
    });

    updateSelectionUI();
  }

  function updateSelectionUI() {
    const count = selectedItems.size;
    document.getElementById('selectedInfo-v3').textContent = `${count} selected`;
    const downloadBtn = document.getElementById('downloadSelected-v3');
    downloadBtn.disabled = count === 0;
    document.getElementById('downloadBtnText-v3').textContent = count > 0
      ? `Download ${count} PDF${count !== 1 ? 's' : ''}`
      : 'Download Selected';

    document.querySelectorAll('.accordion-item-v3').forEach(item => {
      const collectionCb = item.querySelector('.collection-select-all-v3');
      const coaCheckboxes = item.querySelectorAll('.coa-table-v3 .coa-checkbox-v3');
      const total = coaCheckboxes.length;
      const checked = Array.from(coaCheckboxes).filter(cb => cb.checked).length;

      if (checked === 0) {
        collectionCb.checked = false;
        collectionCb.indeterminate = false;
      } else if (checked === total) {
        collectionCb.checked = true;
        collectionCb.indeterminate = false;
      } else {
        collectionCb.checked = false;
        collectionCb.indeterminate = true;
      }
    });

    const selectAllCb = document.getElementById('selectAll-v3');
    const allCoaCheckboxes = document.querySelectorAll('.coa-table-v3 .coa-checkbox-v3');
    const totalAll = allCoaCheckboxes.length;
    const checkedAll = Array.from(allCoaCheckboxes).filter(cb => cb.checked).length;

    if (checkedAll === 0) {
      selectAllCb.checked = false;
      selectAllCb.indeterminate = false;
    } else if (checkedAll === totalAll) {
      selectAllCb.checked = true;
      selectAllCb.indeterminate = false;
    } else {
      selectAllCb.checked = false;
      selectAllCb.indeterminate = true;
    }
  }

  // ===========================================
  // DOWNLOAD FUNCTIONALITY
  // ===========================================
  function sanitizeFilename(str) {
    if (!str) return '';
    return str
      .replace(/[<>:"\/\\|?*\x00-\x1f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .trim();
  }

  function generatePDFFilename(coa, usedFilenames) {
    const parts = [];

    if (coa.batch_number) {
      parts.push(sanitizeFilename(coa.batch_number));
    }

    if (coa.product_title) {
      let product = sanitizeFilename(coa.product_title);
      if (product.length > 40) {
        product = product.substring(0, 40);
      }
      parts.push(product);
    }

    if (coa.variant_title && coa.variant_title !== 'Default Title') {
      let variant = sanitizeFilename(coa.variant_title);
      if (variant.length > 25) {
        variant = variant.substring(0, 25);
      }
      parts.push(variant);
    }

    // Add state info to filename if limited availability
    if (coa.ships_to_states && coa.ships_to_states.length > 0 && coa.ships_to_states.length < 3) {
      parts.push(coa.ships_to_states.join('-'));
    }

    if (coa.sample_id) {
      parts.push(sanitizeFilename(coa.sample_id));
    }

    if (parts.length === 0) {
      parts.push(coa.handle || 'coa');
    }

    let baseFilename = parts.join('_');

    if (baseFilename.length > 100) {
      baseFilename = baseFilename.substring(0, 100);
    }

    let filename = baseFilename + '.pdf';

    if (usedFilenames) {
      let counter = 1;
      while (usedFilenames.has(filename.toLowerCase())) {
        filename = `${baseFilename}_${counter}.pdf`;
        counter++;
      }
      usedFilenames.add(filename.toLowerCase());
    }

    return filename;
  }

  function showToast(message, type = 'info', duration = 4000) {
    const existing = document.querySelector('.toast-v3');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `toast-v3 toast-${type}-v3`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="toast-icon-v3" aria-hidden="true">
        ${type === 'success' ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>' : ''}
        ${type === 'error' ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>' : ''}
        ${type === 'warning' ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>' : ''}
        ${type === 'info' ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>' : ''}
      </span>
      <span class="toast-message-v3">${message}</span>
    `;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('toast-visible-v3'));

    setTimeout(() => {
      toast.classList.remove('toast-visible-v3');
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  function showProgressModal(total) {
    downloadCancelled = false;

    const modal = document.createElement('div');
    modal.className = 'download-modal-v3';
    modal.id = 'downloadModal-v3';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'downloadModalTitle-v3');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = `
      <div class="download-modal-content-v3">
        <h3 id="downloadModalTitle-v3">Downloading PDFs</h3>
        <div class="download-progress-bar-v3" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">
          <div class="download-progress-fill-v3" id="progressFill-v3"></div>
        </div>
        <p class="download-progress-text-v3">
          <span id="progressCurrent-v3">0</span> of <span id="progressTotal-v3">${total}</span> files
        </p>
        <p class="download-current-file-v3" id="currentFile-v3" aria-live="polite">Preparing...</p>
        <button type="button" class="btn-v3 btn-cancel-v3" id="cancelDownload-v3">Cancel</button>
      </div>
    `;
    document.body.appendChild(modal);

    document.getElementById('cancelDownload-v3').addEventListener('click', () => {
      downloadCancelled = true;
      document.getElementById('currentFile-v3').textContent = 'Cancelling...';
    });

    requestAnimationFrame(() => modal.classList.add('modal-visible-v3'));

    return modal;
  }

  function updateProgress(current, total, filename, status = 'downloading') {
    const fill = document.getElementById('progressFill-v3');
    const currentEl = document.getElementById('progressCurrent-v3');
    const fileEl = document.getElementById('currentFile-v3');
    const progressBar = fill?.parentElement;
    const percent = Math.round((current / total) * 100);

    if (fill) fill.style.width = `${percent}%`;
    if (currentEl) currentEl.textContent = current;
    if (fileEl) {
      if (status === 'zipping') {
        fileEl.textContent = 'Creating ZIP file...';
      } else {
        fileEl.textContent = filename ? `Downloading: ${filename}` : 'Processing...';
      }
    }
    if (progressBar) progressBar.setAttribute('aria-valuenow', percent);
  }

  function hideProgressModal() {
    const modal = document.getElementById('downloadModal-v3');
    if (modal) {
      modal.classList.remove('modal-visible-v3');
      setTimeout(() => modal.remove(), 300);
    }
  }

  async function downloadSinglePDF(coa) {
    const downloadUrl = coa.pdf_download_url || coa.pdf_url;
    const viewUrl = coa.pdf_url || coa.pdf_download_url;

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const usedFilenames = new Set();
      const filename = generatePDFFilename(coa, usedFilenames);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast(`Downloaded: ${filename}`, 'success');
    } catch (err) {
      // fetch() failed (likely CORS on external URL) — fall back to opening in new tab
      console.warn('Fetch download failed, opening in new tab:', err);
      window.open(viewUrl, '_blank');
      showToast('PDF opened in new tab — use your browser to save it', 'info');
    }
  }

  async function downloadWithConcurrency(items, concurrency, onProgress) {
    const results = [];
    let currentIndex = 0;
    let completed = 0;

    async function worker() {
      while (currentIndex < items.length && !downloadCancelled) {
        const index = currentIndex++;
        const item = items[index];

        try {
          const result = await item.task();
          results[index] = { success: true, data: result, item: item };
        } catch (err) {
          results[index] = { success: false, error: err, item: item };
        }

        completed++;
        if (onProgress) onProgress(completed, items.length, item.filename);
      }
    }

    const workers = [];
    for (let i = 0; i < Math.min(concurrency, items.length); i++) {
      workers.push(worker());
    }

    await Promise.all(workers);
    return results;
  }

  async function downloadPDFs(coasToDownload, zipName) {
    const withPDF = coasToDownload.filter(c => c.pdf_url || c.pdf_download_url);
    const withoutPDF = coasToDownload.filter(c => !c.pdf_url && !c.pdf_download_url);

    if (withoutPDF.length > 0) {
      showToast(`${withoutPDF.length} item(s) have no PDF and will be skipped`, 'warning', 5000);
    }

    if (withPDF.length === 0) {
      showToast('No PDFs available for selected items', 'error');
      return;
    }

    if (withPDF.length >= LARGE_DOWNLOAD_THRESHOLD) {
      const confirmed = confirm(`You're about to download ${withPDF.length} PDFs. This may take a while. Continue?`);
      if (!confirmed) return;
    }

    downloadCancelled = false;

    if (withPDF.length === 1) {
      await downloadSinglePDF(withPDF[0]);
      return;
    }

    try {
      const JSZip = await loadJSZip();

      const modal = showProgressModal(withPDF.length);

      const zip = new JSZip();
      const usedFilenames = new Set();
      let completed = 0;
      let failed = 0;

      const downloadTasks = withPDF.map(coa => {
        const filename = generatePDFFilename(coa, usedFilenames);
        // Use CDN URL for fetch (CORS-safe), fall back to external URL
        const fetchUrl = coa.pdf_download_url || coa.pdf_url;
        return {
          coa,
          filename,
          task: async () => {
            const response = await fetch(fetchUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await response.blob();
          }
        };
      });

      const results = await downloadWithConcurrency(
        downloadTasks,
        MAX_CONCURRENT_DOWNLOADS,
        (done, total, filename) => {
          updateProgress(done, total, filename);
        }
      );

      if (downloadCancelled) {
        hideProgressModal();
        showToast('Download cancelled', 'info');
        return;
      }

      for (const result of results) {
        if (result && result.success) {
          zip.file(result.item.filename, result.data);
          completed++;
        } else if (result) {
          console.error(`Failed to download ${result.item?.coa?.batch_number}:`, result.error);
          failed++;
        }
      }

      updateProgress(withPDF.length, withPDF.length, null, 'zipping');

      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const dateStr = new Date().toISOString().split('T')[0];
      const zipFilename = zipName || `HalfBakd-COAs_${dateStr}_${completed}-files.zip`;

      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      hideProgressModal();

      if (failed > 0) {
        showToast(`Downloaded ${completed} PDFs. ${failed} failed.`, 'warning', 5000);
      } else {
        showToast(`Successfully downloaded ${completed} PDFs!`, 'success');
      }

    } catch (err) {
      console.error('Download failed:', err);
      hideProgressModal();
      showToast('Failed to create download. Please try again.', 'error');
    }
  }

  async function downloadSelectedPDFs() {
    if (selectedItems.size === 0) return;

    const btn = document.getElementById('downloadSelected-v3');
    const btnText = document.getElementById('downloadBtnText-v3');
    const originalText = btnText.textContent;

    btn.disabled = true;
    btnText.textContent = 'Preparing...';

    const allSelected = allCOAs.filter(c => selectedItems.has(c.handle));

    await downloadPDFs(allSelected);

    btn.disabled = selectedItems.size === 0;
    btnText.textContent = originalText;
  }

  async function downloadCategoryPDFs(catId) {
    const item = document.querySelector(`.accordion-item-v3[data-collection="${catId}"]`);
    if (!item) return;

    const categoryName = item.dataset.category;
    const categoryCOAs = allCOAs.filter(coa => sanitizeCategory(coa.category) === catId);
    const filteredCOAs = getFilteredCOAs(categoryCOAs);

    const dateStr = new Date().toISOString().split('T')[0];
    const safeCatName = sanitizeFilename(categoryName);
    const zipName = `HalfBakd-${safeCatName}_${dateStr}.zip`;

    await downloadPDFs(filteredCOAs, zipName);
  }

  // ===========================================
  // EXPORT TO CSV
  // ===========================================
  function exportToCSV() {
    const filteredCOAs = getFilteredCOAs();

    if (filteredCOAs.length === 0) {
      showToast('No COAs to export', 'warning');
      return;
    }

    // CSV headers
    const headers = [
      'Batch Number',
      'Product',
      'Variant',
      'Sample ID',
      'Category',
      'Collection',
      'Ships To States',
      'PDF URL'
    ];

    // Build CSV rows
    const rows = filteredCOAs.map(coa => [
      coa.batch_number || '',
      coa.product_title || '',
      coa.variant_title !== 'Default Title' ? (coa.variant_title || '') : '',
      coa.sample_id || '',
      coa.category || '',
      coa.collection || '',
      (coa.ships_to_states || []).join(', ') || 'Federal Only',
      coa.pdf_url || ''
    ]);

    // Escape CSV values
    function escapeCSV(value) {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    }

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `HalfBakd-COA-Export_${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showToast(`Exported ${filteredCOAs.length} COAs to CSV`, 'success');
  }

  // ===========================================
  // SEARCH FUNCTIONALITY
  // ===========================================
  function clearSearch() {
    const searchInput = document.getElementById('searchInput-v3');
    if (searchInput) {
      searchInput.value = '';
      searchTerm = '';
      updateURLParams();
      renderCOAs();
      searchInput.focus();
    }
  }

  // ===========================================
  // STICKY BULK BAR & BACK TO TOP
  // ===========================================
  function setupStickyBulkBar() {
    const bulkBar = document.querySelector('.bulk-bar-v3');
    if (!bulkBar) return;

    const placeholder = document.createElement('div');
    placeholder.className = 'bulk-bar-placeholder-v3';
    placeholder.style.display = 'none';

    bulkBar.parentNode.insertBefore(placeholder, bulkBar);

    const bulkBarTop = bulkBar.offsetTop;

    function handleScroll() {
      const scrollY = window.scrollY;

      if (scrollY > bulkBarTop) {
        if (!bulkBar.classList.contains('sticky-v3')) {
          bulkBar.classList.add('sticky-v3');
          placeholder.style.display = 'block';
          placeholder.style.height = bulkBar.offsetHeight + 'px';
        }
      } else {
        bulkBar.classList.remove('sticky-v3');
        placeholder.style.display = 'none';
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  function setupBackToTop() {
    const btn = document.getElementById('backToTop-v3');
    if (!btn) return;

    function handleScroll() {
      if (window.scrollY > BACK_TO_TOP_THRESHOLD) {
        btn.classList.add('visible-v3');
      } else {
        btn.classList.remove('visible-v3');
      }
    }

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // ===========================================
  // DEEP LINKING TO SPECIFIC BATCH
  // ===========================================
  function highlightBatch(batchNumber) {
    if (!batchNumber) return;

    // Find the COA with this batch number
    const coa = allCOAs.find(c =>
      c.batch_number && c.batch_number.toLowerCase() === batchNumber.toLowerCase()
    );

    if (!coa) {
      showToast(`Batch "${batchNumber}" not found`, 'warning');
      return;
    }

    // Get the category and expand it
    const catId = sanitizeCategory(coa.category);
    const accordionItem = document.querySelector(`.accordion-item-v3[data-collection="${catId}"]`);

    if (accordionItem) {
      // Expand the category
      accordionItem.classList.add('expanded-v3');
      const header = accordionItem.querySelector('.accordion-header-v3');
      if (header) header.setAttribute('aria-expanded', 'true');
      expandedCategories.add(catId);

      // Find and highlight the row, then expand parent accordions
      setTimeout(() => {
        const row = document.querySelector(`tr[data-handle="${coa.handle}"]`);
        const card = document.querySelector(`.coa-card-v3[data-handle="${coa.handle}"]`);
        const element = row || card;

        if (element) {
          // Expand parent flavor accordion
          const flavorAccordion = element.closest('.flavor-accordion-v3');
          if (flavorAccordion) {
            flavorAccordion.classList.add('expanded-v3');
            const flavorHeader = flavorAccordion.querySelector('.flavor-header-v3');
            if (flavorHeader) flavorHeader.setAttribute('aria-expanded', 'true');
          }

          // Expand parent variant accordion
          const variantAccordion = element.closest('.variant-accordion-v3');
          if (variantAccordion) {
            variantAccordion.classList.add('expanded-v3');
            const variantHeader = variantAccordion.querySelector('.variant-header-v3');
            if (variantHeader) variantHeader.setAttribute('aria-expanded', 'true');
          }

          // Add highlight class
          element.classList.add('highlighted-v3');

          // Scroll into view after accordions expand
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 50);

          // Remove highlight after animation
          setTimeout(() => {
            element.classList.remove('highlighted-v3');
          }, 3000);
        }
      }, 100);
    }
  }

  // Copy PDF link to clipboard
  function copyPdfLink(pdfUrl, batchNumber) {
    navigator.clipboard.writeText(pdfUrl).then(() => {
      showToast(`PDF link copied for ${batchNumber || 'COA'}!`, 'success');
    }).catch(() => {
      showToast('Failed to copy link', 'error');
    });
  }

  // ===========================================
  // INITIALIZATION
  // ===========================================
  async function init() {
    const loadingIndicator = document.getElementById('loadingIndicator-v3');
    const loadingText = loadingIndicator.querySelector('p');

    restoreFromURL();

    const clearBtn = document.getElementById('clearSearch-v3');
    if (clearBtn) {
      clearBtn.addEventListener('click', clearSearch);
      clearBtn.style.display = searchTerm ? 'flex' : 'none';
    }

    const expandAllBtn = document.getElementById('expandAll-v3');
    const collapseAllBtn = document.getElementById('collapseAll-v3');

    if (expandAllBtn) {
      expandAllBtn.addEventListener('click', expandAllAccordions);
    }
    if (collapseAllBtn) {
      collapseAllBtn.addEventListener('click', collapseAllAccordions);
    }

    // Export to CSV button
    const exportBtn = document.getElementById('exportCSV-v3');
    if (exportBtn) {
      exportBtn.addEventListener('click', exportToCSV);
    }

    // Progressive loading from API
    allCOAs = await fetchAllCOAs((items, batchNum, isComplete) => {
      loadingText.textContent = `Loading certificates... (${items.length} found)`;

      if (batchNum === 1) {
        allCOAs = items;
        renderCOAs();
      }

      if (isComplete) {
        loadingIndicator.classList.add('hidden-v3');
        allCOAs = items;
        renderCOAs();

        // Deep link to specific batch if specified in URL
        const params = getURLParams();
        if (params.batch) {
          setTimeout(() => highlightBatch(params.batch), 200);
        }
      }
    });

    loadingIndicator.classList.add('hidden-v3');

    // State tabs
    document.querySelectorAll('.state-tab-v3').forEach(tab => {
      tab.addEventListener('click', function() {
        document.querySelectorAll('.state-tab-v3').forEach(t => t.classList.remove('active'));
        this.classList.add('active');
        currentFilter = this.dataset.state;
        updateURLParams();
        renderCOAs();
      });
    });

    // Debounced search
    let searchTimeout;
    const searchInput = document.getElementById('searchInput-v3');
    searchInput.addEventListener('input', function() {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        searchTerm = this.value.trim();
        updateURLParams();
        renderCOAs();
      }, 300);
    });

    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        clearSearch();
      }
    });

    // Select all
    document.getElementById('selectAll-v3').addEventListener('change', function() {
      const allCheckboxes = document.querySelectorAll('.coa-table-v3 .coa-checkbox-v3');
      allCheckboxes.forEach(cb => {
        cb.checked = this.checked;
        const handle = cb.dataset.handle;

        // Also update card checkboxes
        document.querySelectorAll(`.coa-cards-v3 .coa-checkbox-v3[data-handle="${handle}"]`).forEach(cardCb => {
          cardCb.checked = this.checked;
        });

        if (this.checked) {
          selectedItems.add(handle);
          document.querySelectorAll(`[data-handle="${handle}"]`).forEach(el => {
            if (el.tagName === 'TR' || el.classList.contains('coa-card-v3')) {
              el.classList.add('selected-v3');
            }
          });
        } else {
          selectedItems.delete(handle);
          document.querySelectorAll(`[data-handle="${handle}"]`).forEach(el => {
            if (el.tagName === 'TR' || el.classList.contains('coa-card-v3')) {
              el.classList.remove('selected-v3');
            }
          });
        }
      });
      updateSelectionUI();
    });

    // Download button
    document.getElementById('downloadSelected-v3').addEventListener('click', downloadSelectedPDFs);

    // Browser back/forward
    window.addEventListener('popstate', function() {
      restoreFromURL();
      renderCOAs();
    });

    // Setup sticky bulk bar and back to top
    setupStickyBulkBar();
    setupBackToTop();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
